import { PatchOperation } from '.';
import { isPlainJSObject } from './isPlainJSObject';
import { Patch } from './Patch';

export function createPatch<T>(a: T, b: T, options: Omit<DiffOptions, 'prefix'> = {}): Patch {
   const operations = diff(a, b, options);
   return { '@@isPatch': true, operations };
}

type DiffOptions = {
   prefix?: string;
   immutablePaths?: string[];
};

function diff<T>(a: T, b: T, options: DiffOptions): PatchOperation[] {
   const operations: PatchOperation[] = [];

   for (let key in a) {
      const path = `${options.prefix ?? ''}/${key}`;
      const before = a[key];
      const after = b[key];
      if (before === after) {
         continue;
      } else if (after === undefined) {
         operations.push({ type: 'remove', path });
      } else if (Array.isArray(after) && after.length === 0) {
         operations.push({ type: 'set', path, value: [] });
      } else if (!isRawDataStructure(before) || !isRawDataStructure(after)) {
         operations.push({ type: 'set', path, value: after });
      } else {
         const nestedOps = diff(before, after, {
            ...options,
            prefix: path,
         });
         const isImmutable = options.immutablePaths?.includes(path);
         if (nestedOps.length && isImmutable) {
            operations.push({ type: 'set', path, value: after });
         } else {
            operations.push(...nestedOps);
         }
      }
   }

   for (let key in b) {
      const path = `${options.prefix ?? ''}/${key}`;
      if (a[key] === undefined) {
         operations.push({ type: 'set', path, value: b[key] });
      }
   }

   return operations;
}

const isRawDataStructure = (x: any) => isPlainJSObject(x) || Array.isArray(x);
